/**
 * secondary-header.js
 * Robust scroll-to-section + active tracking for the secondary header
 */
class SecondaryHeader {
  constructor(options = {}) {
    this.header = document.getElementById('secondary-header');
    this.links = Array.from(document.querySelectorAll('.secondary-header__link'));
    this.activeLink = null;
    this.sections = new Map(); // key: normalized id, value: element
    this.observer = null;
    this.debug = !!options.debug;
    this.init();
  }

  init() {
    if (!this.header || this.links.length === 0) {
      if (this.debug) console.warn('SecondaryHeader: header or links not found');
      return;
    }

    this.buildSectionsMap();
    this.bindEvents();
    this.initObserver();
    this.initInitialActivation();
    this.initHashSync();
    setTimeout(() => this.buildSectionsMap(), 300);
    setTimeout(() => this.buildSectionsMap(), 1000);
    if (this.debug) console.log('SecondaryHeader initialized', { links: this.links.length, sections: this.sections.size });
  }

  normalizeId(id) {
    if (!id) return '';
    return String(id).trim().replace(/^#/, '');
  }

  // Try multiple ways to find the target element
  findSectionElement(sectionId) {
    const id = this.normalizeId(sectionId);
    if (!id) return null;

    // 1) exact id
    let el = document.getElementById(id);
    if (el) return el;

    // 2) id contains (useful if theme adds prefixes)
    try {
      el = document.querySelector(`[id*="${id}"]`);
      if (el) return el;
    } catch (e) {
      // ignore invalid selectors
    }

    // 3) element with data-section attr
    el = document.querySelector(`[data-section="${sectionId}"], [data-section="${id}"]`);
    if (el) return el;

    // 4) class fallback
    el = document.querySelector(`.${id}`);
    if (el) return el;

    // 5) try hyphen/underscore/space variants
    const variants = [
      id.replace(/_/g, '-'),
      id.replace(/_/g, ' '),
      id.replace(/-/g, '_'),
      id.replace(/-/g, ' '),
      id.replace(/\s+/g, '_'),
      id.replace(/\s+/g, '-')
    ];
    for (const v of variants) {
      const elVar = document.getElementById(v) || document.querySelector(`[id*="${v}"]`) || document.querySelector(`.${v}`);
      if (elVar) return elVar;
    }

    return null;
  }

  buildSectionsMap() {
    this.sections.clear();
    this.links.forEach(link => {
      const raw = link.getAttribute('data-section');
      if (!raw) return; // 🔧 skip links without data-section

      const ds = this.normalizeId(raw);
      if (!ds) return;

      const el = this.findSectionElement(ds);
      if (el) {
        this.sections.set(ds, el);
      } else if (this.debug) {
        console.warn(`SecondaryHeader: no target found for data-section="${ds}"`);
      }
    });
  }

  bindEvents() {
    this.links.forEach(link => {
      if (!link.getAttribute('type')) link.setAttribute('type', 'button');

      link.addEventListener('click', (e) => {
        const raw = link.getAttribute('data-section');
        if (!raw) {
          // 🔧 allow normal navigation if no data-section
          return;
        }

        e.preventDefault();
        const id = this.normalizeId(raw);
        window.ActiveWinningCollectionHeroId = id;
        this.activateWinningCollectionForId(id);
        const target = this.sections.get(id) || this.findSectionElement(id);
        if (!target) {
          if (this.debug) console.warn(`SecondaryHeader: Section "${raw}" not found.`);
          return;
        }
        this.setActiveLink(link);
        this.scrollToElement(target);
      });
    });
  }

  getHeaderHeight() {
    const headerComponent = document.getElementById('header-component');
    return headerComponent ? headerComponent.offsetHeight : 60;
  }

  scrollToElement(target) {
    if (!target) return;
    const headerHeight = this.getHeaderHeight();
    const secondaryHeaderHeight = this.header ? this.header.offsetHeight : 60;
    const offset = headerHeight + secondaryHeaderHeight + 20;

    const targetPosition = window.scrollY + target.getBoundingClientRect().top - offset;

    if (this.debug) console.log('Scrolling to', { target, offset, targetPosition });

    window.scrollTo({
      top: Math.max(0, Math.round(targetPosition)),
      behavior: 'smooth'
    });
  }

  setActiveLink(link) {
    if (!link) return;
    if (this.activeLink === link) return;

    this.links.forEach(l => {
      l.classList.remove('active');
      l.removeAttribute('aria-current');
    });

    link.classList.add('active');
    link.setAttribute('aria-current', 'true');
    this.activeLink = link;
    try {
      const id = this.normalizeId(link.getAttribute('data-section'));
      document.dispatchEvent(new CustomEvent('secondary-header:active-updated', { detail: { id } }));
    } catch (e) {}
  }

  initObserver() {
    const elements = Array.from(this.sections.values());
    if (elements.length === 0) {
      if (this.debug) console.warn('SecondaryHeader: no sections to observe');
      return;
    }

    const headerOffset = this.getHeaderHeight() + (this.header ? this.header.offsetHeight : 60);
    const rootMargin = `-${headerOffset}px 0px -40% 0px`;
    const options = { root: null, rootMargin, threshold: 0.1 };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elId = entry.target.id || '';
          const match = this.links.find(link => {
            const ds = this.normalizeId(link.getAttribute('data-section'));
            if (!ds) return false;
            return (elId && (elId === ds || elId.includes(ds))) ||
                   entry.target.matches(`[data-section="${ds}"]`);
          });
          if (match) {
            this.setActiveLink(match);
            const ds = this.normalizeId(match.getAttribute('data-section'));
            window.ActiveWinningCollectionHeroId = ds;
            this.activateWinningCollectionForId(ds);
          }
        }
      });
    }, options);

    elements.forEach(el => this.observer.observe(el));
    if (this.debug) console.log('SecondaryHeader: observer started', { observed: elements.length, rootMargin });
  }

  initInitialActivation() {
    const stored = (() => {
      try { return sessionStorage.getItem('SecondaryHeaderActiveId') || ''; } catch (e) { return ''; }
    })();
    const hash = window.location.hash ? this.normalizeId(window.location.hash) : '';
    const headerEl = document.querySelector('.secondary-header');
    const serverActive = headerEl ? (headerEl.getAttribute('data-active-collection') || '') : '';
    const map = { students: 'Newton_Students', office: 'Newton_Office', pro: 'Newton_Pro' };
    if (hash) {
      this.activateWinningCollectionForId(hash);
      const targetLink = this.links.find(l => this.normalizeId(l.getAttribute('data-section')) === hash);
      if (targetLink) this.setActiveLink(targetLink);
      const targetEl = this.sections.get(hash) || this.findSectionElement(hash);
      if (targetEl) this.scrollToElement(targetEl);
      return;
    }
    if (stored) {
      const id = this.normalizeId(stored);
      this.activateWinningCollectionForId(id);
      const targetLink = this.links.find(l => this.normalizeId(l.getAttribute('data-section')) === id);
      if (targetLink) this.setActiveLink(targetLink);
      return;
    }
    if (serverActive && (!this.sections || this.sections.size === 0)) {
      const id = map[serverActive] || '';
      if (id) {
        const targetLink = this.links.find(l => this.normalizeId(l.getAttribute('data-section')) === id);
        if (targetLink) this.setActiveLink(targetLink);
      }
      return;
    }

    const firstLink = this.links[0];
    if (firstLink) {
      const ds = this.normalizeId(firstLink.getAttribute('data-section'));
      this.activateWinningCollectionForId(ds);
      this.setActiveLink(firstLink);
    }
  }

  initHashSync() {
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash ? this.normalizeId(window.location.hash) : '';
      if (!hash) return;
      const targetLink = this.links.find(l => this.normalizeId(l.getAttribute('data-section')) === hash);
      if (targetLink) {
        this.setActiveLink(targetLink);
        try { sessionStorage.setItem('SecondaryHeaderActiveId', hash); } catch (e) {}
      }
    });
  }

  activateWinningCollectionForId(sectionId) {
    const id = this.normalizeId(sectionId);
    const isDesktop = window.innerWidth > 750;
    const allCollections = document.querySelectorAll('.winning-team-collection');
    allCollections.forEach(col => {
      const assoc = (col.dataset.associatedHero || '').trim();
      const eq = assoc === id;
      const eqIgnore = assoc.replace(/[\s_\-]+/g, '').toLowerCase() === id.replace(/[\s_\-]+/g, '').toLowerCase();
      const isMatch = eq || eqIgnore;
      col.setAttribute('data-autoplay-state', isMatch ? 'active' : 'inactive');
      if (!isMatch) {
        col.querySelectorAll('.winning-team-video').forEach(v => {
          try {
            v.pause();
            v.currentTime = 0;
            const slide = v.closest('.winning-team-slide');
            const thumb = slide?.querySelector('.video-thumbnail-wrapper');
            v.classList.remove('video-ready');
            if (thumb) thumb.classList.remove('video-playing');
          } catch (e) {}
        });
      }
    });

    let activeCollection = null;
    for (const col of document.querySelectorAll('.winning-team-collection')) {
      const assoc = (col.dataset.associatedHero || '').trim();
      const eq = assoc === id;
      const eqIgnore = assoc.replace(/[\s_\-]+/g, '').toLowerCase() === id.replace(/[\s_\-]+/g, '').toLowerCase();
      if (eq || eqIgnore) { activeCollection = col; break; }
    }
    if (activeCollection) {
      const associationEnabled = activeCollection.dataset.associationEnabled === 'true';
      const associationEnabledMobile = activeCollection.dataset.associationEnabledMobile === 'true';
      const cards = activeCollection.querySelectorAll('.winning-team-card');
      if (isDesktop && associationEnabled) {
        window.DisableGlobalWinningAutoplay = true;
        cards.forEach(card => { try { if (window.initVideoAutoplay) window.initVideoAutoplay(card); } catch (e) {} });
      } else if (!isDesktop && associationEnabledMobile) {
        window.DisableGlobalWinningAutoplay = true;
        cards.forEach(card => { try { if (window.initVideoAutoplay) window.initVideoAutoplay(card); } catch (e) {} });
      } else {
        window.DisableGlobalWinningAutoplay = false;
      }
    } else {
      window.DisableGlobalWinningAutoplay = false;
    }
  }
}

// instantiate once DOM is ready
function startSecondaryHeader() {
  new SecondaryHeader({ debug: false });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startSecondaryHeader);
} else {
  startSecondaryHeader();
}


// secondary-header.js
const secondaryHeader = document.querySelector('.secondary-header');
const mainHeader = document.querySelector('#header-component');

function updateSecondaryHeaderTop() {
  if (!mainHeader || !secondaryHeader) return;
  
  const stickyMode = mainHeader.getAttribute('sticky');
  const stickyState = mainHeader.dataset.stickyState;
  
  // For hide-show-scroll mode, only move secondary header down when main header is active
  if (stickyMode === 'hide-show-scroll') {
    if (stickyState === 'active') {
      const headerHeight = mainHeader.offsetHeight;
      secondaryHeader.style.top = headerHeight + 'px';
    } else {
      secondaryHeader.style.top = '0px';
    }
  } else {
    // For other modes, always position below main header
    const headerHeight = mainHeader.offsetHeight;
    secondaryHeader.style.top = headerHeight + 'px';
  }
}

  // Handle compare page active collection detection
  function detectComparePageCollection() {
  if (!window.location.pathname.includes('compare')) return;
  
  const secondaryHeaderEl = document.querySelector('.secondary-header');
  if (!secondaryHeaderEl) return;
  
  // First, check URL parameters for collection info
  const urlParams = new URLSearchParams(window.location.search);
  let detectedCollection = urlParams.get('collection') || '';
  
  // If no URL parameter, look for product data in compare page
  if (!detectedCollection) {
    const productElements = document.querySelectorAll('[data-product-handle], .product-item, .compare-product');
    
    for (const element of productElements) {
      const productHandle = element.dataset ? element.dataset.productHandle : null || 
                           element.querySelector('[data-product-handle]')?.getAttribute('data-product-handle');
      const productTitle = element.querySelector('.product-title, h3, h2')?.textContent || '';
      
      if (productHandle || productTitle) {
        const searchText = (productHandle + ' ' + productTitle).toLowerCase();
        
        if (searchText.includes('student')) {
          detectedCollection = 'students';
          break;
        } else if (searchText.includes('office')) {
          detectedCollection = 'office';
          break;
        } else if (searchText.includes('pro')) {
          detectedCollection = 'pro';
          break;
        }
      }
    }
  }
  
  if (detectedCollection) {
    secondaryHeaderEl.setAttribute('data-active-collection', detectedCollection);
    try {
      const storeId = detectedCollection === 'students' ? 'Newton_Students' : detectedCollection === 'office' ? 'Newton_Office' : 'Newton_Pro';
      sessionStorage.setItem('SecondaryHeaderActiveId', storeId);
    } catch (e) {}
    
    // Detect product type from URL parameters or page content
    let productType = urlParams.get('product') || '';
    if (productType) {
      // Extract just the product model name (first part before color)
      // e.g., "kyra-jet-black" → "kyra"
      productType = productType.split('-')[0];
    }
    
    if (!productType) {
      // Try to get product type from page content
      const productTitleEl = document.querySelector('h1, .product-title, [data-product-title]');
      if (productTitleEl) {
        const titleText = productTitleEl.textContent || '';
        // Extract first word as product type
        productType = titleText.split(' ')[0];
      }
    }
    
    // Reset active state on all links
    const links = secondaryHeaderEl.querySelectorAll('.secondary-header__link');
    links.forEach(link => {
      link.classList.remove('secondary-header__link--active');
      link.classList.remove('active');
      link.removeAttribute('aria-current');
    });
    
    const items = secondaryHeaderEl.querySelectorAll('.secondary-header__item');
    items.forEach(item => item.classList.remove('secondary-header__item--mobile-only'));
    
    if (detectedCollection === 'students') {
      const studentsLink = secondaryHeaderEl.querySelector('[data-section="Newton_Students"]') || 
                          secondaryHeaderEl.querySelector('a[href*="Newton_Students"]');
      if (studentsLink) {
        studentsLink.classList.add('secondary-header__link--active');
        studentsLink.classList.add('active');
        studentsLink.setAttribute('aria-current', 'true');
        const parentItem = studentsLink.closest('.secondary-header__item');
        if (parentItem) {
          parentItem.classList.add('secondary-header__item--mobile-only');
          
          // Update mobile text for compare page
          const mobileTextEl = studentsLink.querySelector('.secondary-header__text-mobile');
          if (mobileTextEl && productType) {
            const productTypeFormatted = productType.replace(/-/g, ' ').toUpperCase();
            mobileTextEl.textContent = `NEWTON STUDENT / ${productTypeFormatted} / השוואה`;
          }
        }
      }
    } else if (detectedCollection === 'office') {
      const officeLink = secondaryHeaderEl.querySelector('[data-section="Newton_Office"]') || 
                        secondaryHeaderEl.querySelector('a[href*="Newton_Office"]');
      if (officeLink) {
        officeLink.classList.add('secondary-header__link--active');
        officeLink.classList.add('active');
        officeLink.setAttribute('aria-current', 'true');
        const parentItem = officeLink.closest('.secondary-header__item');
        if (parentItem) {
          parentItem.classList.add('secondary-header__item--mobile-only');
          
          // Update mobile text for compare page
          const mobileTextEl = officeLink.querySelector('.secondary-header__text-mobile');
          if (mobileTextEl && productType) {
            const productTypeFormatted = productType.replace(/-/g, ' ').toUpperCase();
            mobileTextEl.textContent = `NEWTON OFFICE / ${productTypeFormatted} / השוואה`;
          }
        }
      }
    } else if (detectedCollection === 'pro') {
      const proLink = secondaryHeaderEl.querySelector('[data-section="Newton_Pro"]') || 
                     secondaryHeaderEl.querySelector('a[href*="Newton_Pro"]');
      if (proLink) {
        proLink.classList.add('secondary-header__link--active');
        proLink.classList.add('active');
        proLink.setAttribute('aria-current', 'true');
        const parentItem = proLink.closest('.secondary-header__item');
        if (parentItem) {
          parentItem.classList.add('secondary-header__item--mobile-only');
          
          // Update mobile text for compare page
          const mobileTextEl = proLink.querySelector('.secondary-header__text-mobile');
          if (mobileTextEl && productType) {
            const productTypeFormatted = productType.replace(/-/g, ' ').toUpperCase();
            mobileTextEl.textContent = `NEWTON PRO / ${productTypeFormatted} / השוואה`;
          }
        }
      }
    }
  }
}

updateSecondaryHeaderTop();

// Run compare page detection on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', detectComparePageCollection);
} else {
  detectComparePageCollection();
}

window.addEventListener('resize', updateSecondaryHeaderTop);
window.addEventListener('scroll', updateSecondaryHeaderTop);

// (reverted) do not force-disable global autoplay
